import "./CashClosingReport.css";

const API_URL = "/api";

const MOVEMENT_LABELS = {
  opening: "Apertura",
  income: "Ingreso manual",
  sale_cash: "Venta en efectivo",
  expense: "Egreso manual",
  refund_cash: "Reintegro en efectivo",
  adjustment: "Ajuste",
};

const INCOME_TYPES = [
  "opening",
  "income",
  "sale_cash",
];

function formatCurrency(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) {
    return "No disponible";
  }

  return new Date(value).toLocaleString("es-CO");
}

function getLogoUrl(logo) {
  if (!logo) {
    return "";
  }

  if (
    logo.startsWith("http://") ||
    logo.startsWith("https://") ||
    logo.startsWith("data:")
  ) {
    return logo;
  }

  return `${API_URL}${logo.startsWith("/") ? logo : `/${logo}`}`;
}

function CashClosingReport({ report }) {
  if (!report) {
    return null;
  }

  const difference = Number(report.difference_amount || 0);
  const isBalanced = difference === 0;
  const logoUrl = getLogoUrl(report.company_logo);

  return (
    <article className="cash-closing-report">
      <header className="cash-report-brand">
        {logoUrl && (
          <img
            className="cash-report-logo"
            src={logoUrl}
            alt={`Logo de ${report.company_name}`}
          />
        )}

        <div>
          <p className="cash-report-eyebrow">
            COMPROBANTE OPERATIVO
          </p>

          <h1>{report.company_name || "Mi empresa"}</h1>

          {report.company_nit && (
            <span>NIT: {report.company_nit}</span>
          )}

          {report.company_address && (
            <span>{report.company_address}</span>
          )}

          {(report.company_phone || report.company_email) && (
            <span>
              {[report.company_phone, report.company_email]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
      </header>

      <section className="cash-report-title">
        <p>Cierre y arqueo de caja</p>
        <h2>Comprobante #{report.session_id}</h2>
      </section>

      <section className="cash-report-people">
        <div>
          <span>Abierta por</span>
          <strong>{report.opened_by || "Sistema"}</strong>
          <small>{formatDateTime(report.opened_at)}</small>
        </div>

        <div>
          <span>Cerrada por</span>
          <strong>{report.closed_by || "Sistema"}</strong>
          <small>{formatDateTime(report.closed_at)}</small>
        </div>
      </section>

      <section className="cash-report-summary">
        <div>
          <span>Saldo esperado</span>
          <strong>{formatCurrency(report.expected_amount)}</strong>
        </div>

        <div>
          <span>Efectivo contado</span>
          <strong>{formatCurrency(report.counted_amount)}</strong>
        </div>

        <div className={isBalanced ? "balanced" : "unbalanced"}>
          <span>
            {isBalanced
              ? "Caja cuadrada"
              : difference > 0
                ? "Sobrante"
                : "Faltante"}
          </span>

          <strong>{formatCurrency(Math.abs(difference))}</strong>
        </div>
      </section>

      <section className="cash-report-section">
        <h3>Resumen del arqueo</h3>

        <table className="cash-report-table">
          <tbody>
            <tr>
              <td>Base de apertura</td>
              <td>{formatCurrency(report.opening_amount)}</td>
            </tr>
            <tr>
              <td>Ventas en efectivo</td>
              <td>{formatCurrency(report.sales_income)}</td>
            </tr>
            <tr>
              <td>Ingresos manuales</td>
              <td>{formatCurrency(report.manual_income)}</td>
            </tr>
            <tr>
              <td>Reintegros en efectivo</td>
              <td>-{formatCurrency(report.refunds)}</td>
            </tr>
            <tr>
              <td>Egresos manuales</td>
              <td>-{formatCurrency(report.manual_expense)}</td>
            </tr>
            <tr className="cash-report-total-row">
              <td>Saldo esperado</td>
              <td>{formatCurrency(report.expected_amount)}</td>
            </tr>
            <tr>
              <td>Efectivo contado</td>
              <td>{formatCurrency(report.counted_amount)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {report.denominations?.length > 0 && (
        <section className="cash-report-section">
          <h3>Conteo por denominación</h3>

          <table className="cash-report-table">
            <thead>
              <tr>
                <th>Denominación</th>
                <th>Cantidad</th>
                <th>Subtotal</th>
              </tr>
            </thead>

            <tbody>
              {report.denominations.map((item) => (
                <tr key={item.denomination}>
                  <td>{formatCurrency(item.denomination)}</td>
                  <td>{item.quantity}</td>
                  <td>
                    {formatCurrency(
                      Number(item.denomination) * Number(item.quantity)
                    )}
                  </td>
                </tr>
              ))}

              <tr className="cash-report-total-row">
                <td colSpan="2">Total contado</td>
                <td>{formatCurrency(report.counted_amount)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {report.movements?.length > 0 && (
        <section className="cash-report-section">
          <h3>Movimientos realizados</h3>

          <div className="cash-report-movements">
            {report.movements.map((movement, index) => {
              const movementType =
                movement.movement_type || movement.type;

              const isIncome = INCOME_TYPES.includes(movementType);

              return (
                <div
                  className="cash-report-movement"
                  key={movement.id || `${movementType}-${index}`}
                >
                  <div>
                    <strong>
                      {movement.type ||
                        MOVEMENT_LABELS[movementType] ||
                        movementType}
                    </strong>

                    <span>
                      {movement.concept || "Sin concepto"}
                    </span>

                    {movement.date || movement.created_at ? (
                      <small>
                        {formatDateTime(
                          movement.date || movement.created_at
                        )}
                      </small>
                    ) : null}
                  </div>

                  <b className={isIncome ? "income" : "expense"}>
                    {isIncome ? "+" : "-"}
                    {formatCurrency(movement.amount)}
                  </b>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {report.closing_note && (
        <section className="cash-report-note">
          <strong>Observación de cierre</strong>
          <p>{report.closing_note}</p>
        </section>
      )}

      <footer className="cash-report-footer">
        Documento generado por NetLedger · Control interno
      </footer>
    </article>
  );
}

export default CashClosingReport;